import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';

/**
 * What the library list should draw when it has no posts of its own, from the posts query's state.
 *
 * Only a settled, successful request may say "No results". A pending query that is not fetching
 * is paused rather than finished: React Query pauses a request that is waiting out a retry when
 * the tab is hidden or the device is offline. It reports `isLoading` false and `isError` false
 * then, so keying the empty state on those two alone showed "No results" for a request that had
 * only failed. A paused query that has already failed at least once is therefore an error, and
 * one that has not is still waiting on the network, which is loading.
 */
export type LibraryListPhase = 'loading' | 'error' | 'empty' | 'content';

export function getLibraryListPhase({
  status,
  fetchStatus,
  failureCount,
  hasPosts,
  isPlaceholderData,
}: {
  status: 'pending' | 'error' | 'success';
  fetchStatus: 'fetching' | 'paused' | 'idle';
  failureCount: number;
  hasPosts: boolean;
  /** The posts on screen belong to the previous query, still being replaced. */
  isPlaceholderData: boolean;
}): LibraryListPhase {
  if (hasPosts) {
    return 'content';
  }

  if (status === 'error') {
    return 'error';
  }

  if (status === 'pending') {
    return fetchStatus === 'paused' && failureCount > 0 ? 'error' : 'loading';
  }

  // The previous query's empty result is not this query's answer.
  return isPlaceholderData ? 'loading' : 'empty';
}

/** Marks the list busy while it loads or refreshes: `aria-busy` on web, the busy state on native. */
export function getListBusyProps(isBusy: boolean) {
  return isWeb ? { 'aria-busy': isBusy } : { accessibilityState: { busy: isBusy } };
}
